import { Injectable, EventEmitter } from '@angular/core';
import { Router, NavigationStart, ActivatedRoute, ParamMap } from '@angular/router';
import { DesignFilterSettings } from '../configure/configure-design/DesignFilterSettings';
import { PrewallFilterSettings } from '../configure/configure-prewall/PrewallFilterSettings';
import { RouteToStepIndexMap } from './maps';
import { MilieuSettings } from '../configure/configure-milieu/MilieuSettings';
import { SpecialColorSelection } from '../shared/SpecialColorSelection';
import { IContactData } from "./IContactData";

/** None, normal frame, illuminated frame */
export type FrameQueryParamType = '0' | 'f' | 'i';

/**
 * This interface is meant to have very short field names.
 * See AppStateService.urlQueryParamsMap for details.
 */
export interface IStateQueryParams {
    /** The language identifier. */
    lang?: string;

    /**
     * Whether the trade fair mode (messe-modus) is active. Can be "0" or "1".
     * Trade fair mode disables external links and pdf generation.
     */
    ms?: string;

    /** The current zero-based step. */
    s?: number;

    /** The selected appliance. */
    ap?: 'wc' | 'urinal';

    /** Key of the selected plate. */
    p?: string;

    /** Key of the selected article. */
    a?: string;

    /** Article special color. Available for special article ids only. */
    ac?: string;

    /** The selected frame type. */
    fr?: FrameQueryParamType;

    /** Key of the selected prewall model. */
    pw?: string;

    /** Key of the selected prewall article. */
    pwa?: string;

    /**
     * Whether flexible (step tec schienensystem) was choosen.
     * This has an impact on the resulting material list.
     * Can be "0" or "1".
     */
    fl?: string;

    /**
     * To url used to "navigate away" from the configurator.
     * Use in trade fair mode to go back to the calling site.
     */
    burl?: string;
}

/**
 * Listeners may overwrite the values in this event object to change routing behaviour.
 */
export interface IRouteChangingEvent {
    newRoute: string;
    newState: IStateQueryParams;
}

@Injectable()
export class AppStateService {
    /**
     * If one of these pattern match, the corresponding language is used as the default language.
     */
    private static readonly DomainLangUrlMappings = {
        "localhost": 'de',
        "pim-ww-i": 'de',
        "\\.viega\\.at": 'at',
        "-at\\.(dev|int|qa)\\.web\\.emea\\.dir": 'at',
        "\\.viega\\.com": 'com',
        "-com\\.(dev|int|qa)\\.web\\.emea\\.dir": 'com',
        "\\.viega\\.cz": 'cz',
        "-cz\\.(dev|int|qa)\\.web\\.emea\\.dir": 'cz',
        "\\.viega\\.de": 'de',
        "-de\\.(dev|int|qa)\\.web\\.emea\\.dir": 'de',
        "\\.viega\\.dk": 'dk',
        "-dk\\.(dev|int|qa)\\.web\\.emea\\.dir": 'dk',
        "bedieningsplaatconfigurator\\.viega\\.be": 'nlbe',
        "bedieningsplaatconfigurator-be\\.(dev|int|qa)\\.web\\.emea\\.dir": 'nlbe',
        "\\.viega\\.it": 'it',
        "-it\\.(dev|int|qa)\\.web\\.emea\\.dir": 'it',
        "\\.viega\\.nl": 'nl',
        "-nl\\.(dev|int|qa)\\.web\\.emea\\.dir": 'nl',
        "configurateurdesplaquesdecommande\\.viega\\.be": 'frbe',
        "configurateurdesplaquesdecommande-be\\.(dev|int|qa)\\.web\\.emea\\.dir": 'frbe',
        "\\.viega\\.pl": 'pl',
        "-pl\\.(dev|int|qa)\\.web\\.emea\\.dir": 'pl',
        "\\.viega\\.ru": 'ru',
        "-ru\\.(dev|int|qa)\\.web\\.emea\\.dir": 'ru',
        "\\.viega\\.hu": 'hu',
        "-hu\\.(dev|int|qa)\\.web\\.emea\\.dir": 'hu',
    };
    /** Language used when mapping has failed. */
    private static readonly FallbackLanguage = 'com';

    public routeChanging = new EventEmitter<IRouteChangingEvent>();

    /** Query parameters to be appended to the map produced by urlQueryParamsMap. */
    private pendingQueryParameters: IStateQueryParams;

    public isMobileMenuOpen: boolean = false;

    /**
     * Whether the web-app is in mobile mode or not as decided by the welcome page.
     * Can be: 'mobile' or 'desktop' or falsy
     */
    public displayMode: string;

    public activeStep: number = 0;
    public articleViewsZoomLevel: number = 1;
    public sideBarCollapsed: boolean = false;

    // Preserve some settings for each step. A page reload would remove them though.
    public designSettings = new DesignFilterSettings();
    public prewallSettings = new PrewallFilterSettings();
    public milieuSettings = new MilieuSettings();

    public pdfContactData: IContactData = {};

    public get languageId(): string {
        const languageId = (this.route.snapshot.queryParams as IStateQueryParams).lang || this.getDefaultLanguage();
        return languageId.toString();
    }

    /**
     * The step reflecting the actual progress in the configuration process.
     */
    public get currentStep(): number {
        return Number((this.route.snapshot.queryParams as IStateQueryParams).s);
    }

    /**
     * 'wc' or 'urinal'
     * @returns undefined if no appliance has been selected yet.
     */
    public get selectedAppliance(): 'wc' | 'urinal' {
        return (this.route.snapshot.queryParams as IStateQueryParams).ap;
    }

    /**
     * @returns undefined if no plate has been selected yet.
     */
    public get selectedPlate(): string {
        return (this.route.snapshot.queryParams as IStateQueryParams).p;
    }

    /**
     * @returns undefined if no article has been selected yet.
     */
    public get selectedArticle(): string {
        return (this.route.snapshot.queryParams as IStateQueryParams).a;
    }

    private _specialColorSelection: SpecialColorSelection;
    private _specialColorString: string;
    /**
     * @returns undefined if article is not suitable for special coloring or if the color has not been selected yet.
     */
    public get selectedArticleColor(): SpecialColorSelection {
        const specialColorString = (this.route.snapshot.queryParams as IStateQueryParams).ac;

        if (specialColorString !== this._specialColorString) {
            this._specialColorSelection = SpecialColorSelection.fromString(specialColorString);
            this._specialColorString = specialColorString;
        }
        if (specialColorString === undefined)
            delete this._specialColorSelection;

        return this._specialColorSelection;
    }

    public get frameType(): FrameQueryParamType {
        return (this.route.snapshot.queryParams as IStateQueryParams).fr;
    }

    /**
     * @returns undefined if no prewall has been selected yet.
     */
    public get selectedPrewall(): string {
        return (this.route.snapshot.queryParams as IStateQueryParams).pw;
    }

    /**
     * @returns undefined if no prewall article has been selected yet.
     */
    public get selectedPrewallArticle(): string {
        return (this.route.snapshot.queryParams as IStateQueryParams).pwa;
    }

    private _milieuScreenshotCanvas: HTMLCanvasElement;
    public get milieuScreenshotCanvas(): HTMLCanvasElement {
        return this._milieuScreenshotCanvas;
    }

    public set milieuScreenshotCanvas(newValue: HTMLCanvasElement) {
        this._milieuScreenshotCanvas = newValue;
    }

    public get isFlexiblePrewall(): boolean {
        return (this.route.snapshot.queryParams as IStateQueryParams).fl === "1";
    }

    public get isTradeFairMode(): boolean {
        return (this.route.snapshot.queryParams as IStateQueryParams).ms === "1";
    }

    public get backUrl(): string {
        return (this.route.snapshot.queryParams as IStateQueryParams).burl;
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute) {

        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                const navStartEvent = event as NavigationStart;

                if (navStartEvent.url.startsWith('/mobile'))
                    this.displayMode = 'mobile';
                else if (navStartEvent.url.startsWith('/configure'))
                    this.displayMode = 'desktop';
                else
                    this.displayMode = undefined;

                this.resetPendingQueryParameters();

                let routeUrl = navStartEvent.url;

                // remove query params if any
                const questionMarkIndex = routeUrl.indexOf('?');
                if (questionMarkIndex >= 0)
                    routeUrl = navStartEvent.url.substr(0, questionMarkIndex);

                // determine active configuration by the route
                const stepIndexByRoute: number = RouteToStepIndexMap[routeUrl];
                if (stepIndexByRoute !==  undefined)
                    this.activeStep = stepIndexByRoute;
            }
        },
        console.error);

        this.resetPendingQueryParameters();
    }

    public resetPendingQueryParameters() {
        this.pendingQueryParameters = {};
    }

    /**
     * Takes the index of a configuration segment and returns the corresponding route.
     * @param stepIndex The index of the configuration step.
     */
    public stepIndexToRoute(stepIndex: number): string {
        for (const route of Object.keys(RouteToStepIndexMap)) {
            const index: number = RouteToStepIndexMap[route];

            if (index === stepIndex)
                return route;
        }

        return undefined;
    }

    /**
     * See urlQueryParamsMap.
     * @param newValues The query map extended with one attribute.
     */
    public urlQueryParamsMapExtendedBy(newAttribute: string, newValue: string): IStateQueryParams {
        const newMap = {};
        newMap[newAttribute] = newValue;

        return this.urlQueryParamsMapExtendedWith(newMap);
    }

    /**
     * See urlQueryParamsMap.
     * @param newValues The query map which is extended with the given values object.
     */
    public urlQueryParamsMapExtendedWith(newValues: IStateQueryParams): IStateQueryParams {
        const wouldDecreaseStep = newValues.s !== undefined && newValues.s < this.currentStep;
        if (wouldDecreaseStep)
            delete newValues.s;

        return Object.assign(this.urlQueryParamsMap(), newValues);
    }

    /**
     * Gets the query parameter object for the url.
     * This object holds a subset of the application state parameters and is meant to be passed as "queryParams" when navigating routes.
     * By keeping most of the application's state in the url, it's possible to store the website and the whole configuration in a bookmark or
     * in the browser history.
     */
    public urlQueryParamsMap(): IStateQueryParams {
        let queryMap = {
            lang: this.languageId,
            ms: this.isTradeFairMode ? '1' : '0',
            ap: this.selectedAppliance,
            s: this.currentStep,
            p: this.selectedPlate,
            a: this.selectedArticle,
            ac: this.selectedArticleColor !== undefined ? this.selectedArticleColor.stringRepresentation : undefined,
            fr: this.frameType,
            pw: this.selectedPrewall,
            pwa: this.selectedPrewallArticle,
            fl: this.isFlexiblePrewall ? '1' : '0',
            burl: this.backUrl,
        };

        queryMap = Object.assign(queryMap, this.pendingQueryParameters);
        return queryMap;
    }

    /**
     * See urlQueryParamsBaseMap.
     * @param newValues The query map which is extended with the given values object.
     */
    public urlQueryParamsBaseMapExtendedWith(newValues: IStateQueryParams): IStateQueryParams {
        return Object.assign(this.urlQueryParamsBaseMap(), newValues);
    }

    /**
     * Returns a query params map with the absolute minimum url parameters which should always be present in all navigations, such as the language or
     * whether trade fair mode is enabled.
     */
    public urlQueryParamsBaseMap(): IStateQueryParams {
        let queryMap = {
            lang: this.languageId,
            ms: this.isTradeFairMode ? '1' : '0',
            burl: this.backUrl,
        };

        queryMap = Object.assign(queryMap, this.pendingQueryParameters);
        return queryMap;
    }

    public changeRoute(route: string, state: IStateQueryParams) {
        const event: IRouteChangingEvent = {
            newRoute: route,
            newState: state
        };
        this.routeChanging.emit(event);

        this.router.navigate([event.newRoute], { queryParams: event.newState });
    }

    /**
     * Resets all settings of all config steps.
     */
    public resetSettings() {
        this.designSettings.reset();
        this.prewallSettings.reset();
        this.milieuSettings.reset();
        localStorage.clear();
    }

    /** Prevents pull-down-to-refresh feature of mobile chrome */
    public setMobilePulldown(flag: boolean) {
        document.body.style.setProperty("overflow-y", flag ? "visible" : "hidden");
    }

    public getDefaultLanguage(): string {
        const hostname = window.location.hostname;

        const matchedPattern = Object.keys(AppStateService.DomainLangUrlMappings).find((pattern: string) =>
            new RegExp(pattern, 'i').test(hostname));

        const matchedLang = AppStateService.DomainLangUrlMappings[matchedPattern];
        return matchedLang || AppStateService.FallbackLanguage;
    }
}



// WEBPACK FOOTER //
// ./src/app/core/app-state.service.ts